/* ═══════════════════════════════════════════════════════════════════════════
   THE RULE SETS

   One file per country of operation, the same shape in each, and a lookup.
   The same arrangement as `industries/`, for the same reason: the facts that
   differ between one tenant and the next belong in a table somebody can read
   and correct, not spread through the screens as literals.

   ── The rule this registry exists to enforce ──────────────────────────────

   No file under `surfaces/` may contain a statutory figure. Not 25, not 16,
   not "three days, four times a year". A screen asks the rule set and prints
   what comes back, which is what makes a second country a file rather than a
   search-and-replace through a dozen components — and what stops Norway's
   numbers being shown to a business in Sweden because somebody typed one
   into a JSX file two years ago and nobody remembered.

   ── Only Norway so far, and saying so ─────────────────────────────────────

   There is one file here. Pretending otherwise by shipping a half-checked
   Swedish set would be worse than the gap: a business in Malmö would be
   shown figures that look authoritative and are not. `known()` answers what
   actually exists, `supported()` answers whether a given country does, and
   the screens are written to say "we do not have the rules for this country
   yet" rather than to quietly fall back to Norway's.
   ═══════════════════════════════════════════════════════════════════════ */
import NO from './no.js';

const SETS = { NO };

/** The country this platform was built for, and the fallback for a business
    with nothing recorded. */
export const HOME = 'NO';

/** The country codes there are actual rules for. */
export const known = () => Object.keys(SETS);

/** Is this a country we can answer for at all? */
export const supported = code => Object.hasOwn(SETS, String(code || '').toUpperCase());

/**
 * The rule set for a country.
 *
 * An unknown country falls back to the home set — a screen with no figures
 * at all is worse than one with figures plus the sentence saying whose they
 * are — but `rules(x).code` then differs from what was asked for, and that
 * is how a caller detects it. `supported()` is the question to ask first.
 */
export function rules(code) {
  const key = String(code || '').toUpperCase();
  return SETS[key] || SETS[HOME];
}

/** What a screen prints when it is showing another country's figures. */
export const borrowed = code =>
  supported(code)
    ? null
    : `We do not have the leave rules for this country yet, so the figures `
      + `below are ${SETS[HOME].name}'s. Treat them as an illustration, not `
      + `as this workplace's entitlement.`;
